import isEmpty from 'lodash/isEmpty'
import { ChangeEvent } from 'react'
import { FiClock } from 'react-icons/fi'
import { RiFileListLine } from 'react-icons/ri'
import { customReportsApi } from '~/api/reports'
import { useDeleteSavedReportMutation } from '~/api/reportsV3'
import { formatLocalDateTime } from '~/common/dateUtils'
import { Msg, useMsg } from '~/common/localizationUtils'
import { ItemsListItem } from '~/components/v2/ItemsList'
import {
  LINKS_AND_BUTTONS_EXCLUDING_THE_TITLE,
  useChangeBgColorOnHover
} from '~/components/v2/ResultsLayout/ResultItem'
import { TitleLockupHeading } from '~/components/v2/TitleLockup'
import { REPORTS_HOME_PATH } from '~/custom_reports/constants'
import FavoriteReportCheckbox from '~/custom_reports/home/FavoriteReportCheckbox'
import { ReportListItem } from '~/custom_reports/types/api'
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Flex,
  SimpleGrid,
  Text,
  TextBlockTwoLineMuted,
  useDeleteConfirmModal,
  useErrorModal,
  useSuccessToast
} from '~/design_system'
import { ReportExport } from '~/reports/table/ReportExport'

interface MergedReportItemProps {
  report: ReportListItem
  onSelectReport?: ((event: ChangeEvent<HTMLInputElement>) => void) | undefined
  canExport?: boolean
  canDelete?: boolean
  isSelectable?: boolean
  isSelected?: boolean
  index: number
}

export function MergedReportItem({
  report,
  onSelectReport,
  canExport,
  canDelete,
  isSelectable,
  isSelected,
  index
}: MergedReportItemProps) {
  const msg = useMsg()
  const successToast = useSuccessToast()
  const { showModal: showErrorModal } = useErrorModal()
  const { showModal: showConfirmDeleteModal } = useDeleteConfirmModal()
  const hoverRef = useChangeBgColorOnHover()

  const { mutate: deleteMergedReport, isLoading: isDeleting } = useDeleteSavedReportMutation({
    onSuccess: () => successToast(msg('custom_report.success.delete_saved_report')),
    onError: () => showErrorModal({ message: msg('common.ui.error_processing_request_try_again') })
  })

  const onDelete = async () => {
    const isConfirmed = await showConfirmDeleteModal({
      title: msg('custom_report.confirm.delete_saved_report.title'),
      message: msg('custom_report.confirm.delete_saved_report.message'),
      size: 'xl',
      confirmButtonText: msg('common.ui.delete'),
      cancelButtonText: msg('common.ui.cancel')
    })
    if (isConfirmed) deleteMergedReport({ reportId: report.id })
  }

  return (
    <ItemsListItem
      ref={hoverRef}
      data-testid={`merged-report-link-btn-${index}`}
      sx={{ [LINKS_AND_BUTTONS_EXCLUDING_THE_TITLE]: { zIndex: 2 } }}>
      <Flex justifyContent="space-between" alignItems="center" width="100%" gap={4}>
        <Flex alignItems="center" flex="1" minW={0}>
          <Checkbox
            size="lg"
            mr={2}
            data-testid={`select-merged-report-${index}`}
            onChange={onSelectReport}
            isDisabled={!isSelectable}
            isChecked={isSelected}
            aria-label="Select Report"
          />

          <FavoriteReportCheckbox
            id={report.id}
            name={report.name}
            reportType={report.report_type}
            isFavorite={report.is_favorite}
          />
          <Box ml={6} minW={0} flexBasis="30%">
            <TitleLockupHeading
              href={`${REPORTS_HOME_PATH}/${report.report_type}/${report.id}`}
              data-testid={`merged-report-title-txt-${index}`}>
              {isEmpty(report.name) ? (
                <Text as="span" fontStyle="italic">
                  <Msg id="custom_report.builder.new.title" />
                </Text>
              ) : (
                report.name
              )}
            </TitleLockupHeading>
          </Box>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={10} spacingY={3} ml={6} flex="1" minW={0}>
            <TextBlockTwoLineMuted
              icon={<RiFileListLine />}
              primaryText={msg('common.ui.type')}
              secondaryText={msg('custom_report.merged.report_type')}
              data-testid={`merged-report-type-txt-${index}`}
            />
            <TextBlockTwoLineMuted
              icon={<FiClock />}
              primaryText={msg('custom_report.merged.last_run')}
              secondaryText={
                report.last_ran ? formatLocalDateTime(report.last_ran) : <Msg id="common.ui.na" defaultMessage="N/A" />
              }
              data-testid={`merged-report-last-run-txt-${index}`}
            />
          </SimpleGrid>
        </Flex>
        <ButtonGroup display="flex" flexWrap="wrap" flexShrink={0}>
          {canExport && (
            <ReportExport
              id={report.id}
              report_type={report.report_type}
              name={report.name}
              queryFinder={async () => (await customReportsApi.getSavedReport(report.id)).query}
              queryParams={{ ...report }}
              size="sm"
              hideIcon
              data-testid={`merged-report-export-btn-${index}`}
            />
          )}
          {canDelete && (
            <Button
              variant="deleteSecondary"
              size="sm"
              onClick={onDelete}
              isLoading={isDeleting}
              data-testid={`merged-report-delete-btn-${index}`}>
              <Msg id="common.ui.delete" />
            </Button>
          )}
        </ButtonGroup>
      </Flex>
    </ItemsListItem>
  )
}
